import { useEffect, useMemo, useState } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';
import { useAuth } from '@/context/AuthContext';
import { useOpenAdminPanel } from '@/hooks/useOpenAdminPanel';
import { api } from '@/lib/apiClient';

interface WindowOrg {
  orgId: string;
  orgName: string;
  isPersonal: boolean;
  deadline: Date;
}

interface UsageNumbers {
  membersUsed: number;
  memberCap: number | null;
  groupsUsed: number;
  groupCap: number | null;
}

/**
 * Amber banner for the over-cap 30-day window (PR 6, Task 7; copy upgraded
 * 2026-07-23 per Brandon — name the numbers and both exits so the banner is a
 * task, not an anxiety ticker). Shown to admins of an org that is over its
 * plan's limit but NOT yet paused, while the grace deadline is in the future.
 * Church orgs get an "Update plan →" link; personal orgs are Apple-billed
 * (no web checkout), so remediation copy points at removal/upgrade in the
 * app. Dismiss is per-session only — it reappears on reload.
 */
export default function CapacityWindowBanner() {
  const { organizations } = useAuth();
  const { openAdminPanel, opening } = useOpenAdminPanel();
  const [dismissed, setDismissed] = useState<Set<string>>(() => new Set());
  const [usageByOrg, setUsageByOrg] = useState<Record<string, UsageNumbers>>({});

  const windowOrgs = useMemo((): WindowOrg[] => {
    const now = Date.now();
    const result: WindowOrg[] = [];
    for (const m of organizations) {
      const org = m.organization;
      const isAdmin = m.roles.some((r) => r.role === 'org_admin');
      if (!isAdmin || org.subscription_locked || !org.grace_deadline) continue;
      const deadline = new Date(org.grace_deadline);
      if (Number.isNaN(deadline.getTime()) || deadline.getTime() <= now) continue;
      result.push({
        orgId: m.organization_id,
        orgName: org.name,
        isPersonal: Boolean(org.is_personal),
        deadline,
      });
    }
    return result;
  }, [organizations]);

  // One banner at a time — the shell shows many orgs, but stacking banners is
  // noise. Show the first non-dismissed match.
  const target = windowOrgs.find((o) => !dismissed.has(o.orgId));
  const targetOrgId = target?.orgId ?? null;

  // The banner is admin-only, so it may read the org's usage numbers to make
  // the copy actionable ("12 of 10 members — remove 2..."). Generic copy is
  // the fallback while loading or on failure.
  useEffect(() => {
    if (!targetOrgId || usageByOrg[targetOrgId]) return;
    let cancelled = false;
    api
      .get<{
        success: boolean;
        data: {
          active_members_count: number;
          effective_max_members: number | null;
          active_groups_count: number;
          effective_max_groups: number | null;
        };
      }>(`/organizations/${targetOrgId}/subscription`)
      .then((response) => {
        if (cancelled || !response?.data) return;
        setUsageByOrg((prev) => ({
          ...prev,
          [targetOrgId]: {
            membersUsed: response.data.active_members_count ?? 0,
            memberCap: response.data.effective_max_members,
            groupsUsed: response.data.active_groups_count ?? 0,
            groupCap: response.data.effective_max_groups,
          },
        }));
      })
      .catch(() => {
        // Generic copy remains — never break the shell over banner detail.
      });
    return () => {
      cancelled = true;
    };
  }, [targetOrgId, usageByOrg]);

  if (!target) return null;

  const usage = usageByOrg[target.orgId];
  const overMembers =
    usage?.memberCap && usage.memberCap > 0 ? Math.max(0, usage.membersUsed - usage.memberCap) : 0;
  const overGroups =
    usage?.groupCap && usage.groupCap > 0 ? Math.max(0, usage.groupsUsed - usage.groupCap) : 0;
  const counts: string[] = [];
  if (overMembers > 0) counts.push(`${usage!.membersUsed} of ${usage!.memberCap} members`);
  if (overGroups > 0) counts.push(`${usage!.groupsUsed} of ${usage!.groupCap} groups`);
  const removeCount = overMembers > 0 ? overMembers : overGroups;
  const removeNoun =
    overMembers > 0
      ? overMembers === 1
        ? 'member'
        : 'members'
      : overGroups === 1
        ? 'group'
        : 'groups';
  const upgradeVerb = target.isPersonal ? 'upgrade' : 'update your plan';
  const deadlineLabel = format(target.deadline, 'MMM d');

  return (
    <div className="flex items-start gap-3 border-b border-[#FCD34D] bg-[#FEF3C7] px-4 py-2.5 text-sm text-[#92400E]">
      <div className="min-w-0 flex-1">
        <span aria-hidden="true">⚠️ </span>
        <span className="font-bold">{target.orgName}</span> is over its plan’s limit
        {counts.length > 0 ? (
          <>
            {' '}— <span className="font-bold">{counts.join(' · ')}</span>. Remove {removeCount}{' '}
            {removeNoun} or {upgradeVerb} by <span className="font-bold">{deadlineLabel}</span> to
            keep everything running.
          </>
        ) : (
          <>
            {' '}— new additions are paused.{' '}
            <span className="font-bold">Make changes by {deadlineLabel}</span> to keep everything
            running.
          </>
        )}
        {!target.isPersonal && (
          <button
            onClick={() => openAdminPanel()}
            disabled={opening}
            className="ml-1 whitespace-nowrap font-semibold underline transition hover:text-[#7a3609] disabled:opacity-60"
          >
            {opening ? 'Opening…' : 'Update plan →'}
          </button>
        )}
      </div>
      <button
        onClick={() => setDismissed((prev) => new Set(prev).add(target.orgId))}
        title="Dismiss"
        className="shrink-0 rounded p-1 text-[#92400E] transition hover:bg-[#FCD34D]"
      >
        <XMarkIcon className="h-4 w-4" />
      </button>
    </div>
  );
}
